import { useEffect, useState } from "react";
import { Loader2, X, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

// Admin → Roles: lists the central role master; supports rename/description/ordering/activation
// via PATCH /api/roles/{code}. Role codes themselves are backend-owned and never edited here.
const Roles = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", sort_order: "", active: true });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/roles");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (r) => {
    setForm({
      name: r.name || "",
      description: r.description || "",
      sort_order: r.sort_order != null ? String(r.sort_order) : "",
      active: r.active !== false,
    });
    setFormError("");
    setEditing(r);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    try {
      const payload = { name: form.name.trim() || null, description: form.description.trim() || null, active: form.active };
      if (form.sort_order !== "") payload.sort_order = parseInt(form.sort_order, 10);
      await staffApi.patch(`/roles/${editing.code || editing.id}`, payload);
      toast.success("Role updated.");
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-roles">
      <h1 className="font-display text-2xl font-bold text-slate-900">Roles</h1>
      <p className="mt-1 text-sm text-slate-500">The central role master. Role codes are fixed by the system; you can adjust names, descriptions and ordering.</p>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="roles-error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="roles-empty">No roles returned by the API.</p>
      ) : (
        <div className="mt-6 bg-white border border-slate-200" data-testid="roles-list-card">
          <ul className="divide-y divide-slate-100" data-testid="roles-list">
            {rows.map((r) => (
              <li key={r.code || r.id || r.name} className={`px-5 py-3.5 flex items-center justify-between gap-4 ${r.active === false ? "opacity-50" : ""}`} data-testid={`roles-row-${r.code || r.id || r.name}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 shrink-0 bg-[#0A1128] text-white flex items-center justify-center">
                    <ShieldCheck size={14} className="text-[#E8574F]" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.name || r.code || r}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {[r.code && r.name ? `code: ${r.code}` : "", r.description || "", r.sort_order != null ? `order ${r.sort_order}` : ""].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${r.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {r.active !== false ? "Active" : "Inactive"}
                  </span>
                  {(r.code || r.id) && (
                    <button onClick={() => openEdit(r)} data-testid={`roles-edit-${r.code || r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline">
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="role-form-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">Edit Role {editing.code ? `(${editing.code})` : ""}</h2>
              <button onClick={() => setEditing(null)} data-testid="role-form-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Display Name</label>
                <input data-testid="role-form-name" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Description</label>
                <textarea rows={3} data-testid="role-form-desc" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Sort Order</label>
                <input type="number" data-testid="role-form-sort" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="role-form-active" className="w-4 h-4 accent-[#C5221F]" />
                Active
              </label>
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="role-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="role-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Save Changes
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
